import { useState, useRef, useEffect, useMemo } from "react";
import { Button, Tooltip } from "antd";
import CustomIcon from "components/CustomIcon";
import { MAX_PINNED_STREAMS_ALLOWED, SIGNAL_TYPE } from "../../constants";
import { useDeepDiveContext } from "DeepDiveProvider";
import { UID } from "agora-rtc-react";
import { Joinee, Uids } from "deepdiveSlice";
import { useRTMContext } from "RTMProvider";

interface PinStreamProps {
  userId: UID;
}

const PinStream = ({ userId }: PinStreamProps) => {
  const { t, pinnedStreams, setPinnedStreams, members, joinee } = useDeepDiveContext();
  const isStreamPinned = pinnedStreams.includes(userId);

  // RTM setup
  const { sendSignal } = useRTMContext();
  const {
    joinee: { id },
    uids = {},
  } = (joinee as Joinee) || {};
  const { cameraAudioUid } = (uids as Uids) || {};

  // keep a stable ref to the member object
  const member = useMemo(() => members && members[cameraAudioUid], [members, cameraAudioUid]);
  const memberRef = useRef(member);
  useEffect(() => {
    memberRef.current = member;
  }, [member]);

  // hover & hide-timer state
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimer = () => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  const resetHideTimer = () => {
    clearHideTimer();
    hideTimer.current = setTimeout(() => {
      setVisible(false);
    }, 2000);
  };

  const onMouseEnter = () => {
    setVisible(true);
    resetHideTimer();
  };
  const onMouseMove = () => {
    if (visible) {
      resetHideTimer();
    } else {
      setVisible(true);
      resetHideTimer();
    }
  };
  const onMouseLeave = () => {
    clearHideTimer();
    setVisible(false);
  };

  useEffect(() => {
    return () => clearHideTimer();
  }, []);

  // send layout-changed RTM signal
  const sendPinnedLayoutChangedSignal = (newPinnedStreams: UID[]) => {
    sendSignal(
      SIGNAL_TYPE.PINNED_LAYOUT_CHANGED,
      cameraAudioUid,
      id,
      memberRef.current,
      undefined,
      undefined,
      undefined,
      false,
      undefined,
      undefined,
      undefined,
      newPinnedStreams
    );
  };

  // pin/unpin toggle
  const togglePin = () => {
    if (!isStreamPinned && pinnedStreams.length < MAX_PINNED_STREAMS_ALLOWED) {
      const next = [...pinnedStreams, userId];
      setPinnedStreams(next);
      sendPinnedLayoutChangedSignal(next);
    } else if (isStreamPinned) {
      const next = pinnedStreams.filter((u) => u !== userId);
      setPinnedStreams(next);
      sendPinnedLayoutChangedSignal(next);
    }
  };

  return (
    <div className='pin-icon' onMouseEnter={onMouseEnter} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
      <div className='pin-icon-inner'>
        <div
          className='pin-icon-content'
          style={{
            opacity: visible ? 1 : 0,
            transition: "opacity 0.2s ease-in-out",
          }}
        >
          <Tooltip
            placement='left'
            title={
              !isStreamPinned
                ? pinnedStreams.length < MAX_PINNED_STREAMS_ALLOWED
                  ? t("PIN_STREAM")
                  : t("MAX_PINNED_STREAMS_REACHED", {
                      nbr: MAX_PINNED_STREAMS_ALLOWED,
                    })
                : t("UNPIN_STREAM")
            }
          >
            <Button type='text' size='small' className='color-white' onClick={togglePin}>
              <CustomIcon name={!isStreamPinned ? "pin_camera" : "unpin_camera"} fill='#ffffff' />
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default PinStream;
